import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  AlertTriangle,
  CalendarDays,
  Clock,
  Loader2,
  Megaphone,
  MessageSquareQuote,
  Star,
  Users,
} from "lucide-react";
import { useDisplayData } from "@/hooks/use-display";
import { formatDate, formatTime } from "@/lib/i18n";
import type {
  DisplayQueueEntry,
  WaitingRoomAnnouncement,
  WaitingRoomData,
  WaitingRoomReview,
  WaitingRoomScheduleEntry,
} from "@ease/shared";

const REVIEW_ROTATION_MS = 15_000;

function renderCapacityLabel(session: WaitingRoomScheduleEntry): string {
  if (session.maxParticipants === null) {
    return `${session.enrolledCount} enrolled`;
  }

  if (session.availableSpots === 0) {
    return `${session.enrolledCount}/${session.maxParticipants} full`;
  }

  return `${session.availableSpots} spots left`;
}

export default function WaitingRoomDisplay() {
  const [searchParams] = useSearchParams();
  const displayId = searchParams.get("displayId") ?? "";
  const token = searchParams.get("token") ?? "";
  const [reviewIndex, setReviewIndex] = useState(0);

  const { data, isLoading, error, isStale, lastUpdatedAt } = useDisplayData<WaitingRoomData>(displayId, token, "waiting-room");

  const queue: readonly DisplayQueueEntry[] = data?.queue ?? [];
  const reviews: readonly WaitingRoomReview[] = data?.reviews ?? [];
  const schedule: readonly WaitingRoomScheduleEntry[] = data?.schedule ?? [];
  const announcements: readonly WaitingRoomAnnouncement[] = data?.announcements ?? [];

  useEffect(() => {
    if (reviews.length <= 1) {
      setReviewIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setReviewIndex((current) => (current + 1) % reviews.length);
    }, REVIEW_ROTATION_MS);

    return () => clearInterval(interval);
  }, [reviews.length]);

  const featuredReview = useMemo(() => {
    if (reviews.length === 0) {
      return null;
    }
    return reviews[reviewIndex % reviews.length] ?? reviews[0] ?? null;
  }, [reviewIndex, reviews]);

  if (!displayId || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_42%),linear-gradient(180deg,var(--color-background),color-mix(in_oklab,var(--color-background)_88%,black))] p-12">
        <p className="text-2xl text-[var(--color-muted-foreground)]">Display not configured. Provide displayId and token.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_42%),linear-gradient(180deg,var(--color-background),color-mix(in_oklab,var(--color-background)_88%,black))]">
        <Loader2 className="h-12 w-12 animate-spin text-[var(--color-primary)]" aria-label="Loading display data" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_42%),linear-gradient(180deg,var(--color-background),color-mix(in_oklab,var(--color-background)_88%,black))] p-12">
        <div className="space-y-4 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-[var(--color-destructive)]" aria-label="Error" />
          <p className="text-2xl text-[var(--color-destructive)]">Unable to load display data</p>
          <p className="text-lg text-[var(--color-muted-foreground)]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.16),_transparent_38%),linear-gradient(180deg,color-mix(in_oklab,var(--color-background)_92%,black),var(--color-background))] px-8 py-8 text-[var(--color-foreground)] md:px-12">
      {isStale && (
        <div className="mx-auto mb-6 max-w-5xl rounded-2xl border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 px-6 py-3 text-center">
          <p className="text-lg text-[var(--color-warning)]">Data may be outdated. Reconnecting...</p>
        </div>
      )}

      <div className="mx-auto flex max-w-7xl items-end justify-between gap-6">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--color-primary)]">
            <Users className="h-3.5 w-3.5" aria-hidden="true" />
            Live Lobby View
          </div>
          <h1 className="text-5xl font-semibold tracking-[-0.04em] md:text-6xl">Waiting Room</h1>
          <p className="mt-3 max-w-2xl text-lg text-[var(--color-muted-foreground)]">
            Live arrivals, upcoming group sessions, and social proof for the next clients walking through the door.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-right backdrop-blur">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--color-muted-foreground)]">Last updated</div>
          <div className="mt-1 text-lg font-semibold">
            {lastUpdatedAt ? formatTime(lastUpdatedAt) : "..."}
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 grid max-w-7xl gap-6 xl:grid-cols-[1.45fr_1fr]">
        <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur">
          <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-[-0.03em]">Live Queue</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Confirmed arrivals and active appointments update in real time.</p>
            </div>
            <div className="rounded-full border border-white/10 bg-black/15 px-3 py-1 text-sm text-[var(--color-muted-foreground)]">
              {queue.length} active
            </div>
          </div>

          <div className="mt-5 grid gap-4">
            {queue.length === 0 ? (
              <div className="rounded-[1.5rem] border border-dashed border-white/15 bg-black/10 px-8 py-16 text-center">
                <Clock className="mx-auto mb-4 h-12 w-12 text-[var(--color-muted-foreground)]" aria-hidden="true" />
                <p className="text-2xl text-[var(--color-muted-foreground)]">No appointments right now</p>
              </div>
            ) : (
              queue.map((item, index) => (
                <article
                  key={`${item.position}-${index}`}
                  className={`grid gap-3 rounded-[1.75rem] border px-5 py-5 transition ${
                    item.status === "in_progress"
                      ? "border-emerald-400/30 bg-emerald-400/10"
                      : item.status === "next"
                        ? "border-amber-300/35 bg-amber-300/10"
                        : "border-white/10 bg-black/10"
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-xl font-semibold">
                        {item.position}
                      </div>
                      <div>
                        <div className="text-2xl font-semibold tracking-[-0.03em]">{item.name}</div>
                        <div className="text-sm text-[var(--color-muted-foreground)]">{item.service}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-[0.22em] text-[var(--color-muted-foreground)]">ETA</div>
                      <div className="mt-1 text-2xl font-semibold">
                        {item.estimatedWait === 0 ? "Now" : `~${item.estimatedWait}m`}
                      </div>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

        <div className="grid gap-6">
          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em]">Guest Love</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Recent public 4- and 5-star reviews rotate every 15 seconds.</p>
              </div>
              <div className="inline-flex items-center gap-1 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-sm font-medium text-amber-100">
                <Star className="h-4 w-4 fill-current" aria-hidden="true" />
                {featuredReview ? `${featuredReview.rating.toFixed(1)}/5` : "Live"}
              </div>
            </div>

            {featuredReview ? (
              <div className="mt-5 rounded-[1.75rem] border border-white/10 bg-black/10 p-5">
                <MessageSquareQuote className="h-8 w-8 text-[var(--color-primary)]" aria-hidden="true" />
                <p className="mt-4 text-2xl leading-snug tracking-[-0.02em]">&ldquo;{featuredReview.quote}&rdquo;</p>
                <div className="mt-5 flex items-center justify-between gap-4 text-sm text-[var(--color-muted-foreground)]">
                  <span>{featuredReview.author}</span>
                  <span>{formatDate(featuredReview.publishedAt)}</span>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-[1.75rem] border border-dashed border-white/15 bg-black/10 px-6 py-10 text-center text-[var(--color-muted-foreground)]">
                Positive public reviews will appear here once they are available.
              </div>
            )}
          </section>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 text-[var(--color-primary)]" aria-hidden="true" />
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em]">Today&apos;s Group Sessions</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Upcoming and live classes pulled from the live service-delivery schedule.</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              {schedule.length === 0 ? (
                <div className="rounded-[1.5rem] border border-dashed border-white/15 bg-black/10 px-6 py-8 text-center text-[var(--color-muted-foreground)]">
                  No group sessions are scheduled today.
                </div>
              ) : (
                schedule.map((session) => (
                  <article key={session.id} className="rounded-[1.5rem] border border-white/10 bg-black/10 px-4 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-lg font-semibold">{session.serviceName}</div>
                        <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                          {session.startTime} - {session.endTime}
                        </div>
                      </div>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${
                          session.status === "in_progress"
                            ? "bg-emerald-400/15 text-emerald-100"
                            : session.status === "starting_soon"
                              ? "bg-amber-300/15 text-amber-100"
                              : "bg-white/10 text-[var(--color-muted-foreground)]"
                        }`}
                      >
                        {session.status === "in_progress"
                          ? "Live now"
                          : session.status === "starting_soon"
                            ? "Starts soon"
                            : "Later today"}
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-[var(--color-muted-foreground)]">{renderCapacityLabel(session)}</div>
                  </article>
                ))
              )}
            </div>
          </section>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur">
            <div className="flex items-center gap-3">
              <Megaphone className="h-5 w-5 text-[var(--color-primary)]" aria-hidden="true" />
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em]">Announcements</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Display-authored notices from the waiting-room signage configuration.</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3">
              {announcements.length === 0 ? (
                <div className="rounded-[1.5rem] border border-dashed border-white/15 bg-black/10 px-6 py-8 text-center text-[var(--color-muted-foreground)]">
                  No custom announcements are configured for this display.
                </div>
              ) : (
                announcements.map((announcement) => (
                  <article key={announcement.id} className="rounded-[1.5rem] border border-white/10 bg-black/10 px-4 py-4">
                    <div className="text-lg font-semibold">{announcement.title}</div>
                    <p className="mt-2 text-sm leading-6 text-[var(--color-muted-foreground)]">{announcement.body}</p>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
